import React, { useMemo, useState } from 'react';
import {
  Badge,
  Box, Button, FormControl, FormLabel, HStack, IconButton, Menu, MenuButton, MenuDivider,
  MenuItem, MenuList, Modal, ModalBody, ModalCloseButton, ModalContent, ModalFooter, ModalHeader,
  ModalOverlay, Portal, Select, Text, Tooltip, useDisclosure, useToast,
} from '@chakra-ui/react';
import {
  FiCopy, FiDownload, FiEdit2, FiEye, FiMoreVertical, FiSend, FiArrowRightCircle,
} from 'react-icons/fi';
import axios from 'axios';
import { NIL, v4 } from 'uuid';
import { QueryStatus } from 'react-query';
import { useHistory } from 'react-router-dom';
import IconPurchaseOrders from '../assets/Icon-PurchaseOrders.svg';
import { PurchaseOrder, PurchaseOrderStatus } from '../models/purchaseOrder';
import usePurchaseOrder from '../hooks/usePurchaseOrder';
import useProjects from '../hooks/useProjects';
import useCurrentUser from '../hooks/useCurrentUser';
import DocumentGridShell from './DocumentGridShell';
import { useConfirm } from './ConfirmProvider'; // item 1500 — row-level confirm
import LedgerTable, { LedgerColumn, LedgerBulkAction } from './LedgerTable';
import LedgerSubline from './LedgerSubline';
import ProjectOptionsByCustomer from './ProjectOptionsByCustomer'; // item 1454
import { LEDGER_W } from './ledgerGrid';
import { isArchivedRow } from '../utils/archived'; // item 714
import StatusPill from './StatusPill';
import SendDocumentButton from './forms/SendDocumentButton';
import PurchaseOrderConvertModal from './PurchaseOrderConvertModal';
// item 576 — the PO send copy moved server-side (DocumentEmailCopy) with the other doc types
import openPdf, { previewPdf } from '../utils/openPdf';
import { money } from '../utils/money';
import { canArchivePurchaseOrder } from '../utils/archiveRules';
import { SqlDateTimeToDisplay } from '../utils/SqlTools'; // item 1320

// item 553 — Purchase Orders on the shared LEDGER (mirrors Invoices, item 547). POs are created
// directly (ADD) and sent to the VENDOR. ONE primary action: Send to vendor (branded email to the
// vendor's Orders address). Read-only status pill; status transitions + copy-to-project live in ⋮.
// POs archive (no void endpoint), matching the legacy grid.

// item 553 — a PO is just Created (draft) → Ordered (once sent). No accountant/payment flow
// (payment lives on the Bill). Any non-Draft status reads as "Ordered".
const ORDERED = PurchaseOrderStatus.NewEntered; // canonical "ordered" marker (any non-Draft)
const poStatusLabel = (value: PurchaseOrderStatus): string => (value === PurchaseOrderStatus.Draft ? 'Created' : 'Ordered');
// item 1069 — the list endpoint serializes the projected CostTotal as `costTotal`; the old
// `total` read never existed on the wire, so every row showed $0.00
const amountOf = (po: PurchaseOrder): number => (po as any).costTotal ?? (po as any).total ?? 0;

interface PurchaseOrdersGridProps {
  data?: PurchaseOrder[];
  status: QueryStatus;
  projectId?: string;
  collapse?: { collapsed: boolean; onToggle: () => void };
}

const PurchaseOrdersGrid: React.FC<PurchaseOrdersGridProps> = (props) => {
  const {
    data, status, projectId, collapse,
  } = props;
  const inProject = !!projectId && projectId !== NIL;
  const {
    tryArchive, tryVoid, voidMany, trySetStatus, tryUpdate: tryAddPurchaseOrder, fetchById,
  } = usePurchaseOrder({ id: NIL });
  const { canModerate } = useCurrentUser();
  const { data: projects } = useProjects();
  const history = useHistory();
  const toast = useToast();
  // item 1500 (Chad) — the row ⋮ Archive fired with NO confirm (the bulk bar asks; one row
  // didn't). Same shared dialog + wording family as the bulk bar.
  const confirm = useConfirm();
  const confirmArchive = async (po: PurchaseOrder) => {
    if (!(await confirm(
      `Archive ${po.purchaseOrderNumber || 'this purchase order'}? It leaves the ledger (the Archived filter still shows it).`,
      { title: 'Archive purchase order', confirmLabel: 'Archive', destructive: true },
    ))) return;
    tryArchive(po.id);
  };

  const copyModal = useDisclosure();
  const [copyId, setCopyId] = useState('');
  const [copyProjectId, setCopyProjectId] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'created' | 'ordered'>('all');
  // item 553 — "Send to vendor" opens the real branded email dialog (routes to the vendor's
  // Orders email); no pay link for POs.
  const [sendId, setSendId] = useState('');
  const [sendSignal, setSendSignal] = useState(0);

  const onPdfError = () => toast({ status: 'error', title: 'Could not open the PDF' });
  const previewPo = (id: string) => previewPdf(`PurchaseOrder/${id}/pdf`).catch(onPdfError);
  const downloadPo = (id: string) => openPdf(`PurchaseOrder/${id}/pdf`).catch(onPdfError);
  const tryEdit = (id: string) => history.push(`/purchaseOrder/${id}`);

  // item 733F (Chad) — convert a PO into a customer-facing Sales Order (default) or Work Order,
  // priced at GROUP (their price), with an optional per-doc HARDWARE discount off group. Opens the
  // new doc; the client hardware PO # carries through. Services are never discounted.
  const convertModal = useDisclosure();
  const [convertId, setConvertId] = useState('');

  // item 813 — the shared PurchaseOrderConvertModal owns the SO/WO choice + discount + POST.
  const openConvertModal = (id: string) => { setConvertId(id); convertModal.onOpen(); };
  const openCopyModal = (id: string) => { setCopyId(id); setCopyProjectId(''); copyModal.onOpen(); };
  const sendToVendor = (po: PurchaseOrder) => {
    setSendId(po.id);
    // item 576 — subject/body pre-fill from the server's shared copy rule (GET email/copy)
    setSendSignal((s) => s + 1);
  };

  const duplicatePurchaseOrder = async (id: string) => {
    try {
      const original = await fetchById(id);
      if (!original) return;
      const copy = { ...original };
      copy.id = NIL;
      copy.purchaseOrderNumber = '';
      copy.purchaseOrderItems.forEach((i) => { i.id = v4(); });
      copy.isVoid = false;
      copy.isArchive = false;
      copy.isLocked = false;
      await tryAddPurchaseOrder(copy);
    } catch {
      toast({ status: 'error', title: 'Action failed', description: 'The document was NOT saved.' });
    }
  };

  const copyToProject = async () => {
    if (!copyProjectId) return;
    try {
      const original = await fetchById(copyId);
      if (!original) return;
      const copy = { ...original };
      copy.id = NIL;
      copy.projectId = copyProjectId;
      copy.purchaseOrderNumber = '';
      copy.purchaseOrderItems.forEach((i) => { i.id = v4(); });
      copy.isVoid = false;
      copy.isArchive = false;
      copy.isLocked = false;
      await tryAddPurchaseOrder(copy);
      toast({ status: 'success', title: 'Copied to project' });
    } catch {
      toast({ status: 'error', title: 'Action failed', description: 'The document was NOT saved.' });
    }
    copyModal.onClose();
  };

  const columns: LedgerColumn<PurchaseOrder>[] = useMemo(() => [
    {
      // item 1445 (Chad, after the 1444 invoice fold: "I thought we were doing a pass on all the ledgers") —
      // PURCHASE ORDER = the portal pattern: number bold, PO name as two-line subtext, then the
      // project outside a project + the item-1320 SENT stamp. NUMBER + PO NAME folded into one
      // flexible column (300px floor); VENDOR keeps its own column.
      // item 1447 — PO name + project ride the full-width rowSubline; number + SENT here.
      key: 'number',
      header: 'PURCHASE ORDER',
      width: LEDGER_W.id, // item 1449 — shared column positions (see ledgerGrid.ts)
      sortValue: (po) => po.purchaseOrderNumber ?? '',
      render: (po) => (
        <Box minW={0}>
          <Text fontWeight="semibold" whiteSpace="nowrap">{po.purchaseOrderNumber ?? '—'}</Text>
          {/* item 1320 (Chad) — SENT stamp, same treatment as invoices (item 777) */}
          {po.sentDate && (
          <Badge colorScheme="green" variant="subtle" fontSize="0.6rem" mt={1}>
            {`SENT · ${SqlDateTimeToDisplay(po.sentDate)}`}
          </Badge>
          )}
        </Box>
      ),
    },
    {
      // item 1331 (Chad: "do we have room to add a date") — CREATED date; the ledger's default sort
      // (newest on top) so the chevron has a column to sit on. Number order lies for revisions.
      key: 'created',
      header: 'CREATED',
      width: LEDGER_W.created,
      sortValue: (po) => (po as { createdDate?: string; lastModifiedDate?: string }).createdDate ?? (po as { lastModifiedDate?: string }).lastModifiedDate ?? '',
      render: (po) => {
        const d = (po as { createdDate?: string; lastModifiedDate?: string }).createdDate ?? (po as { lastModifiedDate?: string }).lastModifiedDate;
        return <span style={{ whiteSpace: 'nowrap' }}>{d ? SqlDateTimeToDisplay(d) : '—'}</span>;
      },
    },
    // item 1319's PO NAME column (job name + project + SENT) folded under the number — item 1445; VENDOR stays its own column.
    {
      key: 'vendor',
      header: 'VENDOR',
      width: LEDGER_W.slot3,
      sortValue: (po) => (po as any).vendor?.name ?? (po as any).vendorName ?? '',
      render: (po) => <Text noOfLines={1}>{(po as any).vendor?.name ?? (po as any).vendorName ?? '—'}</Text>,
    },
    {
      key: 'amount', header: 'AMOUNT', width: LEDGER_W.slot4, align: 'right', sortValue: (po) => amountOf(po), render: (po) => money(amountOf(po)),
    },
    {
      key: 'status',
      header: 'STATUS',
      width: LEDGER_W.status,
      sortValue: (po) => po.status,
      render: (po) => <StatusPill label={poStatusLabel(po.status)} isVoid={po.isVoid} minW="118px" />,
    },
  ], []);

  const iconSpacer = <Box w="24px" flexShrink={0} />;
  const previewBtn = (po: PurchaseOrder) => (
    <Tooltip label="Preview" openDelay={300}>
      <IconButton size="xs" variant="ghost" aria-label="Preview" icon={<FiEye />} onClick={() => previewPo(po.id)} />
    </Tooltip>
  );
  const downloadBtn = (po: PurchaseOrder) => (
    <Tooltip label="Download PDF" openDelay={300}>
      <IconButton size="xs" variant="ghost" aria-label="Download PDF" icon={<FiDownload />} onClick={() => downloadPo(po.id)} />
    </Tooltip>
  );

  const rowActions = (po: PurchaseOrder): React.ReactNode => {
    // a VOID PO stays viewable (view/download only); POs archive rather than void
    if (po.isVoid) {
      return (
        <HStack spacing={1} justify="flex-end">
          {iconSpacer}
          {previewBtn(po)}
          {downloadBtn(po)}
          {iconSpacer}
        </HStack>
      );
    }
    return (
      <HStack spacing={1} justify="flex-end">
        {/* item 801 (Chad) — before it's ordered the primary is SEND (to the vendor); once
            ordered the job is placed, so the primary becomes CONVERT TO SALES ORDER and the
            resend lives under ⋮ ("Send to vendor"). */}
        {po.status === PurchaseOrderStatus.Draft ? (
          <Button size="xs" minW="76px" colorScheme="brandGreen" onClick={() => sendToVendor(po)}>Send</Button>
        ) : (
          <Button size="xs" colorScheme="brandGreen" leftIcon={<FiArrowRightCircle />} onClick={() => openConvertModal(po.id)}>Convert</Button>
        )}
        <Tooltip label="Duplicate" openDelay={300}>
          <IconButton size="xs" variant="ghost" aria-label="Duplicate" icon={<FiCopy />} onClick={() => duplicatePurchaseOrder(po.id)} />
        </Tooltip>
        {previewBtn(po)}
        {downloadBtn(po)}
        <Menu isLazy>
          <MenuButton as={IconButton} size="xs" variant="ghost" aria-label="More actions" icon={<FiMoreVertical />} />
          <Portal>
            <MenuList fontSize="sm">
              <MenuItem icon={<FiEdit2 />} onClick={() => tryEdit(po.id)}>Edit</MenuItem>
              <MenuItem icon={<FiSend />} onClick={() => sendToVendor(po)}>Send to vendor</MenuItem>
              {/* item 733F (Chad) — convert to a customer-facing doc, priced at group */}
              <MenuItem icon={<FiArrowRightCircle />} onClick={() => openConvertModal(po.id)}>Convert to Sales Order</MenuItem>
              <MenuItem icon={<FiArrowRightCircle />} onClick={() => openConvertModal(po.id)}>Convert to Work Order</MenuItem>
              <MenuDivider />
              {/* item 553 — manual toggle for phone/verbal orders (Send auto-marks Ordered) */}
              {po.status === PurchaseOrderStatus.Draft
                ? <MenuItem onClick={() => trySetStatus(po.id, ORDERED)}>Mark as ordered</MenuItem>
                : <MenuItem onClick={() => trySetStatus(po.id, PurchaseOrderStatus.Draft)}>Mark as created</MenuItem>}
              <MenuDivider />
              <MenuItem onClick={() => openCopyModal(po.id)}>Copy to project…</MenuItem>
              {/* item 1500 — confirm first, exactly like the bulk bar */}
              {/* EPIC_VOID_FLOW_2026-10 — POs get a void (reason + optional vendor notice); archive only once void */}
              {canModerate && !po.isVoid && <MenuItem onClick={() => { tryVoid(po.id); }}>Void</MenuItem>}
              {canModerate && canArchivePurchaseOrder(po) && <MenuItem onClick={() => confirmArchive(po)}>Archive</MenuItem>}
            </MenuList>
          </Portal>
        </Menu>
      </HStack>
    );
  };

  const bulkActions: LedgerBulkAction<PurchaseOrder>[] = canModerate ? [
    {
      label: 'Archive',
      appliesTo: (po) => !isArchivedRow(po) && canArchivePurchaseOrder(po), // item 1570 — void first
      confirm: (rows) => `Archive ${rows.length} purchase order(s)?`,
      onRun: async (rows) => { await Promise.all(rows.map((po) => tryArchive(po.id))); },
    },
    {
      label: 'Void',
      colorScheme: 'red',
      appliesTo: (po) => !po.isVoid,
      onRun: async (rows) => { await voidMany(rows.map((po) => po.id)); }, // EPIC_VOID_FLOW — one dialog
    },
  ] : [];

  const rows = useMemo(() => {
    const list = (data ?? []).filter((po) => !isArchivedRow(po));
    if (statusFilter === 'created') return list.filter((po) => po.status === PurchaseOrderStatus.Draft && !po.isVoid);
    if (statusFilter === 'ordered') return list.filter((po) => po.status !== PurchaseOrderStatus.Draft && !po.isVoid);
    return list;
  }, [data, statusFilter]);

  return (
    <>
      <DocumentGridShell
        heading="PURCHASE ORDERS"
        icon={IconPurchaseOrders}
        addPath={`/purchaseOrder/${NIL}${inProject ? `?projectId=${projectId}` : ''}`}
        collapsible={!!collapse}
        collapsed={collapse?.collapsed}
        onToggleCollapse={collapse?.onToggle}
        status={status}
        // item 712 (Chad) — header count must match the visible (archived-filtered) rows
        data={data === undefined ? undefined : rows}
      >
        <LedgerTable<PurchaseOrder>
          defaultSort={{ key: 'created', dir: -1 }} // item 1331 (Chad) — DATE order, newest on top (number order lies for revisions)
          rows={rows}
          columns={columns}
          rowSubline={(po) => <LedgerSubline name={po.serviceName} meta={inProject ? [] : [(po as any).projectName]} />}
          rowId={(po) => po.id}
          rowActions={rowActions}
          rowActionsWidth={LEDGER_W.actions}
          rowActionsFlex
          rowActionsHeader="ACTION"
          bulkActions={bulkActions}
          selectable={canModerate}
          filters={(
            <Select size="sm" maxW="200px" variant="filled" borderRadius="lg" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value as typeof statusFilter)}>
              <option value="all">ALL STATUSES</option>
              <option value="created">CREATED</option>
              <option value="ordered">ORDERED</option>
            </Select>
          )}
          searchText={(po) => `${po.purchaseOrderNumber ?? ''} ${(po as any).vendor?.name ?? (po as any).vendorName ?? ''} ${po.serviceName ?? ''}`}
          searchPlaceholder="Search purchase orders…"
          onRowClick={(po) => tryEdit(po.id)}
          emptyText="No purchase orders yet."
        />
      </DocumentGridShell>
      {/* item 553 — branded "Send to vendor" email (routes to the vendor's Orders address) */}
      <SendDocumentButton
        hideTrigger
        documentType="PurchaseOrder"
        documentId={sendId}
        autoOpenSignal={sendSignal}
        onSent={() => { if (sendId) trySetStatus(sendId, ORDERED); }}
      />
      {/* copy-to-project (mirrors the Proposals grid) */}
      <Modal isOpen={copyModal.isOpen} onClose={copyModal.onClose} isCentered>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Copy purchase order to another project</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl isRequired>
              <FormLabel fontSize="sm">Destination project</FormLabel>
              <Select placeholder="Select a project" value={copyProjectId} onChange={(e) => setCopyProjectId(e.target.value)}>
                {/* item 1454 — grouped under the CUSTOMER */}
                <ProjectOptionsByCustomer projects={projects?.index} />
              </Select>
            </FormControl>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={copyModal.onClose}>Cancel</Button>
            <Button colorScheme="brandGreen" isDisabled={!copyProjectId} onClick={copyToProject}>Copy</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* item 733F/813 (Chad) — shared convert modal (also on the PO form footer) */}
      <PurchaseOrderConvertModal
        isOpen={convertModal.isOpen}
        onClose={convertModal.onClose}
        purchaseOrderId={convertId}
      />
    </>
  );
};

PurchaseOrdersGrid.defaultProps = {
  data: undefined,
  projectId: NIL,
  collapse: undefined,
};

export default PurchaseOrdersGrid;
